/**
 * The "Get payroll ready" checklist query (GET /api/v1/payroll/setup) and
 * the one helper every mutation that moves it calls.
 *
 * Browser walkthrough 2026-10: the checklist kept showing old steps after
 * the TAN / PF / ESIC codes were saved, an employee or their salary was
 * added, "Include in payroll" was toggled, a spreadsheet import was
 * committed, the PT state changed — even after a full page reload, because
 * none of those mutations flushed it and the query cache is persisted to
 * localStorage with a 5-minute staleTime.
 *
 * Two fixes, both pinned by lib/payroll-setup-queries.test.ts:
 *   1. every mutation that changes what the checklist reads calls
 *      invalidatePayrollSetup;
 *   2. the query is never persisted (shouldPersistQuery) and always
 *      refetches on mount — it is cheap and drives a decision.
 *
 * Plain .ts with type-only imports so the node unit tests can load it.
 */

/** react-query key of GET /api/v1/payroll/setup. */
export const PAYROLL_SETUP_QUERY_KEY = ["payroll", "setup"] as const;

/** The slice of QueryClient this helper needs. */
export interface InvalidatingClient {
  invalidateQueries(filters: { queryKey: readonly unknown[] }): Promise<void>;
}

/** Refetch the payroll checklist. */
export function invalidatePayrollSetup(qc: InvalidatingClient): void {
  void qc.invalidateQueries({ queryKey: PAYROLL_SETUP_QUERY_KEY });
}

/** Ask for Details key family (hooks/use-detail-requests.ts detailKeys.all).
 *  HR's check view carries the employee's full sent values (bank account,
 *  PAN, …) and item versions: never in localStorage, never stale. */
export const DETAIL_REQUESTS_QUERY_KEY = ["detail-requests"] as const;

/** Query keys that are never written to the localStorage cache: a reload
 *  must show the server's answer, not a snapshot from before a save. */
const NOT_PERSISTED: readonly (readonly unknown[])[] = [PAYROLL_SETUP_QUERY_KEY, DETAIL_REQUESTS_QUERY_KEY];

function startsWith(key: readonly unknown[], prefix: readonly unknown[]): boolean {
  return prefix.length <= key.length && prefix.every((p, i) => key[i] === p);
}

/**
 * Persister filter (PersistQueryClientProvider dehydrateOptions): keeps
 * react-query's default (only successful queries) and drops the keys
 * above.
 */
export function shouldPersistQuery(query: {
  queryKey: readonly unknown[];
  state: { status: string };
}): boolean {
  if (query.state.status !== "success") return false;
  return !NOT_PERSISTED.some((k) => startsWith(query.queryKey, k));
}
